"use client";

import { c as _c } from "react/compiler-runtime";
import { createContext, type ReactNode, use, useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { FloatingChatStatePersistence } from "./floating-persistence";

/**
 * How the floating chat surface presents itself, mirroring the Notion AI
 * layout switcher: a docked full-height `sidebar`, a corner `floating` card,
 * or `fullscreen`. Runtime state (the user toggles between these live via the
 * layout menu), so it's lifted into the provider rather than passed as static
 * props — every part reads the current presentation from context. See
 * `vercel-composition-patterns` (state-lift-state).
 */
export type FloatingChatPresentation = "floating" | "sidebar" | "fullscreen";

/** Corner the launcher + floating/expanded panel anchor to. */
export type FloatingChatSide = "bottom-right" | "bottom-left" | "top-right" | "top-left";
export const FLOATING_CHAT_SIDES: readonly FloatingChatSide[] = ["bottom-right", "bottom-left", "top-right", "top-left"];

/** Narrows a stored string to a corner — for persistence seams reading storage. */
export function isFloatingChatSide(value: string | null | undefined): value is FloatingChatSide {
  return FLOATING_CHAT_SIDES.some(side => side === value);
}

/**
 * Host chrome the launcher and floating panel keep clear of, as CSS lengths
 * added to their 1rem corner gap (safe-area insets are added on top) — e.g.
 * a top bar, or a phone's bottom tab bar.
 */
export interface FloatingChatInsets {
  bottom?: string;
  top?: string;
}

/** Docked sidebar width bounds (px). Drag-resizable between these. */
export const SIDEBAR_MIN_WIDTH = 320;
export const SIDEBAR_MAX_WIDTH = 720;
export const SIDEBAR_DEFAULT_WIDTH = 420;
export interface FloatingChatState {
  open: boolean;
  presentation: FloatingChatPresentation;
  /**
   * Live docked-sidebar width in px (drag-resizable). Lives on the root —
   * not panel-local state — because the persistent trigger must offset
   * itself by the same width while the sidebar is docked, and a future
   * persistence seam wants to remember the user's resize.
   */
  sidebarWidth: number;
}
export interface FloatingChatActions {
  close: () => void;
  open: () => void;
  setPresentation: (presentation: FloatingChatPresentation) => void;
  /**
   * Move the launcher (and floating panel) to a corner. A no-op unless the
   * root is `draggable`.
   */
  setSide: (side: FloatingChatSide) => void;
  setSidebarWidth: (px: number) => void;
  toggle: () => void;
}
export interface FloatingChatMeta {
  /**
   * Presentations the layout menu offers. `undefined` = all of them
   * (today's behavior). With one entry (a locked `mode`) the menu renders
   * nothing. Resolved on the root (`allowedPresentations`).
   */
  allowedPresentations?: readonly FloatingChatPresentation[];
  /**
   * Corner anchors keep clear of `insets` and the device's safe areas. On
   * whenever the root is `draggable` or has `insets`, so hosts using neither
   * keep their exact layout.
   */
  clearsChrome: boolean;
  /**
   * Close the floating popup when the user clicks outside it. Resolved on
   * the root (`dismissOnOutsideClick`, default `true`); only the floating
   * presentation honors it — the docked sidebar is persistent chrome and
   * the sheet has its own scrim.
   */
  dismissOnOutsideClick: boolean;
  /** The launcher can be moved to another corner (root `draggable`). */
  draggable: boolean;
  /** Host chrome to keep clear of (root `insets`). */
  insets?: FloatingChatInsets;
  /**
   * Host interceptor for the default header's layout menu (e.g. route "Full
   * screen" to a dedicated page). Return `false` to suppress the switch.
   */
  onLayoutSelect?: (presentation: FloatingChatPresentation) => boolean | undefined;
  /** Root `onOpenInChat`: replaces the layout menu's "Full screen". */
  onOpenInChat?: () => void;
  /** Stable id pairing the trigger's `aria-controls` with the panel. */
  panelId: string;
  side: FloatingChatSide;
}

/**
 * The conversation-history data the default header renders, injected on
 * `<FloatingChat.Root history={...}>`. Structurally identical to the chat
 * block's `ChatHistorySource` (duplicated so this block stays dependency-free)
 * — any object satisfying one satisfies the other.
 */
export interface FloatingChatHistorySource {
  items: {
    id: string;
    title?: string | null;
    updatedAt: string;
  }[];
  loading?: boolean;
  onNewChat: () => void;
  onSelect: (chatId: string) => void;
  /**
   * Refetch the list. The default header's title menu calls this on open
   * (so a failed or stale initial load self-heals) and wires it to the
   * refresh affordance next to "New chat". Return a promise settling when
   * the fetch does so the menu can spin the affordance for exactly the
   * explicit refresh — not the store-wide loading flag (a hung background
   * re-poll must never spin it).
   */
  refresh?: () => Promise<void> | void;
  /**
   * Subscribe to list changes (`items` / `loading`); returns unsubscribe.
   * The title menu subscribes locally while mounted so the open dropdown
   * tracks the store even if an ancestor stops propagating updates.
   */
  subscribe?(listener: () => void): () => void;
}

/**
 * Data the shell renders but does not own. The chat session itself stays with
 * the host; these are the read models the default header needs.
 */
export interface FloatingChatSources {
  /** Currently open conversation — drives the title menu's label + check. */
  activeChatId?: string;
  /** Conversation list + selection callbacks. Absent → static title. */
  history?: FloatingChatHistorySource;
}

/**
 * Generic interface any provider can implement — the contract the compound
 * `FloatingChat.*` parts depend on. Lets a host swap in its own open/
 * presentation state (URL, global store, ...) without touching the UI.
 */
export interface FloatingChatContextValue {
  actions: FloatingChatActions;
  meta: FloatingChatMeta;
  sources: FloatingChatSources;
  state: FloatingChatState;
}
const FloatingChatContext = createContext<FloatingChatContextValue | null>(null);
export function useFloatingChat(): FloatingChatContextValue {
  const ctx = use(FloatingChatContext);
  if (!ctx) {
    throw new Error("useFloatingChat must be used within <FloatingChat.Root>");
  }
  return ctx;
}

/** Read the context without throwing — for optional integrations. */
export function useFloatingChatOptional(): FloatingChatContextValue | null {
  return use(FloatingChatContext);
}
export interface FloatingChatRootProps {
  /** Currently open conversation — the default header labels + checks it. */
  activeChatId?: string;
  /**
   * Restrict which presentations the layout menu offers. Omit for all
   * three. A single-entry list (or any list of one) hides the menu — used
   * by locked `mode` hosts.
   */
  allowedPresentations?: readonly FloatingChatPresentation[];
  children: ReactNode;
  /** Uncontrolled initial open state. Defaults to closed. */
  defaultOpen?: boolean;
  /** Uncontrolled initial presentation. Defaults to `"floating"`. */
  defaultPresentation?: FloatingChatPresentation;
  /**
   * Close the floating popup when the user clicks outside it. Defaults to
   * `true`. Only the `floating` presentation dismisses — the docked sidebar
   * is persistent chrome and the sheet's scrim always closes.
   */
  dismissOnOutsideClick?: boolean;
  /**
   * Let the user move the launcher to any corner, by dragging it or from the
   * layout menu. A drag snaps to the nearest corner on release, the floating
   * panel follows, and the corner is remembered through `statePersistence`
   * (`side` in the snapshot). `side` becomes the starting corner. Off by
   * default.
   */
  draggable?: boolean;
  /**
   * Conversation list for the default header's title dropdown. When omitted
   * the header degrades to a static title.
   */
  history?: FloatingChatHistorySource;
  /** Host chrome the launcher and floating panel keep clear of. */
  insets?: FloatingChatInsets;
  /**
   * Intercept layout-menu choices — e.g. route "Full screen" to a dedicated
   * chat page instead of the overlay presentation. Return `false` to suppress
   * the built-in `setPresentation`.
   */
  onLayoutSelect?: (presentation: FloatingChatPresentation) => boolean | undefined;
  onOpenChange?: (open: boolean) => void;
  /**
   * Hand the conversation to the host's full chat page. When set, the layout
   * menu offers "Open in chat" in place of the full-screen overlay.
   */
  onOpenInChat?: () => void;
  onPresentationChange?: (presentation: FloatingChatPresentation) => void;
  /**
   * Controlled open state. Pass with `onOpenChange` to own it externally —
   * e.g. a button in the host's own nav, outside this provider. A persisted
   * open flag is handed to `onOpenChange` on mount rather than applied
   * directly, and host-driven changes are written back to the seam, so a
   * controlled host stays refresh-proof.
   */
  open?: boolean;
  /** Controlled presentation. Pass with `onPresentationChange` to own it. */
  presentation?: FloatingChatPresentation;
  /** Corner anchor for the launcher + floating/expanded panel. */
  side?: FloatingChatSide;
  /**
   * Optional state-persistence seam. `read()` is applied once on mount:
   * directly for uncontrolled state, and via `onOpenChange` for a controlled
   * `open` (a controlled `presentation` is a lock — it neither hydrates nor
   * persists). `write()` fires on every open/presentation change, whether it
   * came from the shell's controls or from a controlled host, and on every
   * corner move of a `draggable` root (carrying `side`). Use
   * `createFloatingUrlPersistence()` for a
   * router-free URL + localStorage implementation, or bind your router.
   */
  statePersistence?: FloatingChatStatePersistence;
}

/**
 * Provider + state owner for the floating chat surface. Holds open +
 * presentation state (uncontrolled by default, fully controllable via props)
 * and exposes it through the {@link FloatingChatContextValue} triad, so the
 * launcher, panel, and custom host controls all read/drive the same state
 * without prop-drilling — they only need to be within this provider.
 */
export function FloatingChatRoot(t0) {
  const $ = _c(72);
  const {
    activeChatId,
    allowedPresentations,
    children,
    defaultOpen: t1,
    defaultPresentation: t2,
    dismissOnOutsideClick: t3,
    draggable: t4,
    history,
    insets,
    onLayoutSelect,
    onOpenInChat,
    open: openProp,
    onOpenChange,
    presentation: presentationProp,
    onPresentationChange,
    side: t5,
    statePersistence
  } = t0;
  const defaultOpen = t1 === undefined ? false : t1;
  const defaultPresentation = t2 === undefined ? "floating" : t2;
  const dismissOnOutsideClick = t3 === undefined ? true : t3;
  const draggable = t4 === undefined ? false : t4;
  const sideProp = t5 === undefined ? "bottom-right" : t5;
  const panelId = useId();
  const [openState, setOpenState] = useState(defaultOpen);
  const [presentationState, setPresentationState] = useState(defaultPresentation);
  const [sidebarWidth, setSidebarWidth] = useState(SIDEBAR_DEFAULT_WIDTH);
  const [movedSide, setMovedSide] = useState();
  const side = draggable && movedSide || sideProp;
  const isOpenControlled = openProp !== undefined;
  const open = isOpenControlled ? openProp : openState;
  const isPresentationControlled = presentationProp !== undefined;
  const presentation = isPresentationControlled ? presentationProp : presentationState;
  const hydratedRef = useRef(false);
  const previousOpenRef = useRef(openProp);
  let t6;
  if ($[0] !== allowedPresentations || $[1] !== draggable || $[2] !== isOpenControlled || $[3] !== isPresentationControlled || $[4] !== onOpenChange || $[5] !== openProp || $[6] !== statePersistence) {
    t6 = () => {
      if (!statePersistence || hydratedRef.current) {
        return;
      }
      hydratedRef.current = true;
      const snapshot = statePersistence.read();
      if (draggable && isFloatingChatSide(snapshot.side)) {
        setMovedSide(snapshot.side);
      }
      if (snapshot.open !== undefined) {
        if (isOpenControlled) {
          if (snapshot.open !== openProp) {
            onOpenChange?.(snapshot.open);
          }
        } else {
          setOpenState(snapshot.open);
        }
      }
      if (!isPresentationControlled && snapshot.presentation !== undefined && (!allowedPresentations || allowedPresentations.includes(snapshot.presentation))) {
        setPresentationState(snapshot.presentation);
      }
    };
    $[0] = allowedPresentations;
    $[1] = draggable;
    $[2] = isOpenControlled;
    $[3] = isPresentationControlled;
    $[4] = onOpenChange;
    $[5] = openProp;
    $[6] = statePersistence;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== statePersistence) {
    t7 = [statePersistence];
    $[8] = statePersistence;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  useLayoutEffect(t6, t7);
  let t8;
  let t9;
  if ($[10] !== isPresentationControlled || $[11] !== openProp || $[12] !== presentation || $[13] !== statePersistence) {
    t8 = () => {
      if (openProp === undefined || !statePersistence) {
        return;
      }
      if (previousOpenRef.current === openProp) {
        return;
      }
      previousOpenRef.current = openProp;
      statePersistence.write({
        open: openProp,
        presentation: isPresentationControlled ? undefined : presentation
      });
    };
    t9 = [isPresentationControlled, openProp, presentation, statePersistence];
    $[10] = isPresentationControlled;
    $[11] = openProp;
    $[12] = presentation;
    $[13] = statePersistence;
    $[14] = t8;
    $[15] = t9;
  } else {
    t8 = $[14];
    t9 = $[15];
  }
  useEffect(t8, t9);
  let t10;
  if ($[16] !== isOpenControlled || $[17] !== isPresentationControlled || $[18] !== onOpenChange || $[19] !== presentation || $[20] !== statePersistence) {
    t10 = next => {
      if (isOpenControlled) {
        onOpenChange?.(next);
        return;
      }
      setOpenState(next);
      onOpenChange?.(next);
      statePersistence?.write({
        open: next,
        presentation: isPresentationControlled ? undefined : presentation
      });
    };
    $[16] = isOpenControlled;
    $[17] = isPresentationControlled;
    $[18] = onOpenChange;
    $[19] = presentation;
    $[20] = statePersistence;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  const setOpen = t10;
  let t11;
  if ($[22] !== allowedPresentations || $[23] !== isPresentationControlled || $[24] !== onPresentationChange || $[25] !== open || $[26] !== statePersistence) {
    t11 = next_0 => {
      if (allowedPresentations && !allowedPresentations.includes(next_0)) {
        return;
      }
      if (!isPresentationControlled) {
        setPresentationState(next_0);
      }
      onPresentationChange?.(next_0);
      statePersistence?.write({
        open,
        presentation: next_0
      });
    };
    $[22] = allowedPresentations;
    $[23] = isPresentationControlled;
    $[24] = onPresentationChange;
    $[25] = open;
    $[26] = statePersistence;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  const setPresentation = t11;
  let t12;
  if ($[28] !== draggable || $[29] !== isPresentationControlled || $[30] !== open || $[31] !== presentation || $[32] !== side || $[33] !== statePersistence) {
    t12 = next_1 => {
      if (!draggable || next_1 === side) {
        return;
      }
      setMovedSide(next_1);
      statePersistence?.write({
        open,
        presentation: isPresentationControlled ? undefined : presentation,
        side: next_1
      });
    };
    $[28] = draggable;
    $[29] = isPresentationControlled;
    $[30] = open;
    $[31] = presentation;
    $[32] = side;
    $[33] = statePersistence;
    $[34] = t12;
  } else {
    t12 = $[34];
  }
  const setSide = t12;
  let t13;
  let t14;
  if ($[35] !== setOpen) {
    t13 = () => setOpen(true);
    t14 = () => setOpen(false);
    $[35] = setOpen;
    $[36] = t13;
    $[37] = t14;
  } else {
    t13 = $[36];
    t14 = $[37];
  }
  let t15;
  if ($[38] !== open || $[39] !== setOpen) {
    t15 = () => setOpen(!open);
    $[38] = open;
    $[39] = setOpen;
    $[40] = t15;
  } else {
    t15 = $[40];
  }
  let t16;
  if ($[41] !== setPresentation || $[42] !== setSide || $[43] !== t13 || $[44] !== t14 || $[45] !== t15) {
    t16 = {
      open: t13,
      close: t14,
      toggle: t15,
      setPresentation,
      setSide,
      setSidebarWidth
    };
    $[41] = setPresentation;
    $[42] = setSide;
    $[43] = t13;
    $[44] = t14;
    $[45] = t15;
    $[46] = t16;
  } else {
    t16 = $[46];
  }
  const actions = t16;
  const clearsChrome = draggable || Boolean(insets);
  let t17;
  if ($[47] !== activeChatId || $[48] !== history) {
    t17 = {
      activeChatId,
      history
    };
    $[47] = activeChatId;
    $[48] = history;
    $[49] = t17;
  } else {
    t17 = $[49];
  }
  const sources = t17;
  let t18;
  if ($[50] !== open || $[51] !== presentation || $[52] !== sidebarWidth) {
    t18 = {
      open,
      presentation,
      sidebarWidth
    };
    $[50] = open;
    $[51] = presentation;
    $[52] = sidebarWidth;
    $[53] = t18;
  } else {
    t18 = $[53];
  }
  let t19;
  if ($[54] !== allowedPresentations || $[55] !== clearsChrome || $[56] !== dismissOnOutsideClick || $[57] !== draggable || $[58] !== insets || $[59] !== onLayoutSelect || $[60] !== onOpenInChat || $[61] !== panelId || $[62] !== side) {
    t19 = {
      allowedPresentations,
      clearsChrome,
      dismissOnOutsideClick,
      draggable,
      insets,
      panelId,
      side,
      onLayoutSelect,
      onOpenInChat
    };
    $[54] = allowedPresentations;
    $[55] = clearsChrome;
    $[56] = dismissOnOutsideClick;
    $[57] = draggable;
    $[58] = insets;
    $[59] = onLayoutSelect;
    $[60] = onOpenInChat;
    $[61] = panelId;
    $[62] = side;
    $[63] = t19;
  } else {
    t19 = $[63];
  }
  let t20;
  if ($[64] !== actions || $[65] !== sources || $[66] !== t18 || $[67] !== t19) {
    t20 = {
      state: t18,
      actions,
      meta: t19,
      sources
    };
    $[64] = actions;
    $[65] = sources;
    $[66] = t18;
    $[67] = t19;
    $[68] = t20;
  } else {
    t20 = $[68];
  }
  const value = t20;
  let t21;
  if ($[69] !== children || $[70] !== value) {
    t21 = <FloatingChatContext value={value}>{children}</FloatingChatContext>;
    $[69] = children;
    $[70] = value;
    $[71] = t21;
  } else {
    t21 = $[71];
  }
  return t21;
}
export interface FloatingChatSourcesProviderProps {
  /** Currently open conversation — the default header labels + checks it. */
  activeChatId?: string;
  children: ReactNode;
  /** Conversation list + selection callbacks for the default header. */
  history?: FloatingChatHistorySource;
}

/**
 * Overlay that swaps the shell's `sources` for a subtree while inheriting
 * state/actions/meta from the enclosing `<FloatingChat.Root>`. Used by
 * `MagnusFloatingChat`, whose conversation hook must live INSIDE the panel:
 * the panel portals to `document.body` and mounts a commit after the shell,
 * so its fibers stay live under React 19.2 StrictMode + a Suspense boundary
 * in the host subtree (e.g. `next/dynamic`) — where a component above the
 * root can have its store subscription bound to a discarded fiber and never
 * re-render (facebook/react#35550), freezing selection, hydration, and
 * title updates in the UI.
 */
export function FloatingChatSourcesProvider(t0) {
  const $ = _c(9);
  const {
    activeChatId,
    children,
    history
  } = t0;
  const outer = useFloatingChat();
  let t1;
  if ($[0] !== activeChatId || $[1] !== history) {
    t1 = {
      activeChatId,
      history
    };
    $[0] = activeChatId;
    $[1] = history;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const sources = t1;
  let t2;
  if ($[3] !== outer || $[4] !== sources) {
    t2 = {
      ...outer,
      sources
    };
    $[3] = outer;
    $[4] = sources;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const value = t2;
  let t3;
  if ($[6] !== children || $[7] !== value) {
    t3 = <FloatingChatContext value={value}>{children}</FloatingChatContext>;
    $[6] = children;
    $[7] = value;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
}